import React, { useMemo } from 'react'

import { Button } from '@wartek-id/button-v1'
import { Text } from '@wartek-id/text'

import useSchoolContext from 'app/KinerjaKSPage/hooks/useSchoolContext'
import { useKemudahanSurveyDialog } from 'app/KinerjaShared/hooks/use-kemudahan-survey-dialog'
import { useKemudahanSurveyTrackEvent } from 'app/KinerjaShared/hooks/use-kemudahan-survey-track-event'
import { SurveyDialog } from 'app/KinerjaShared/components/SurveyDialog'

import type { KinerjaTipe } from 'types/kinerja'

import ImgTermConditionTNCList from 'assets/icons/img-term-condition-tnc-list.svg'

interface TabRangkumanReadyProps {
  tipe?: KinerjaTipe
  onClickBackToHome?: () => any
}

const TabRangkumanReady = ({
  tipe,
  onClickBackToHome = () => {},
}: TabRangkumanReadyProps) => {
  const schoolCtx = useSchoolContext()
  const isPltKSRole =
    schoolCtx && schoolCtx.selectedSchool
      ? schoolCtx.selectedSchool.role === 'KS_PLT' ||
        schoolCtx.selectedSchool.role === 'KS_PLT_SPNF'
      : false

  // ------------------------------
  // Handle Kemudahan Survey Dialog
  // ------------------------------
  const {
    isOpen: isSurveyDialogOpen,
    isEnabled: isSurveyEnabled,
    isSubmitted: isSurveySubmitted,
    open: openSurveyDialog,
    close: closeSurveyDialog,
    markAsSubmitted: markAsSubmittedSurvey,
  } = useKemudahanSurveyDialog({
    type: 'perencanaan-kepala-skb',
    additionalKey: schoolCtx.selectedSchool?.npsn,
  })

  const { trackEvent: trackSurveyEvent } = useKemudahanSurveyTrackEvent()

  const handleSubmitSurvey = (selected) => {
    trackSurveyEvent({
      actor: 'kepala_skb',
      eventAction: 'submit',
      eventCategory: 'survey_kemudahan_perencanaan',
      customDimensions: {
        likert_value: String(selected.value),
        likert_label: selected.label,
      },
    })
  }

  const handleCloseSurveyDialog = () => {
    closeSurveyDialog()
    onClickBackToHome()
  }

  const handleOpenSurveyDialog = () => {
    openSurveyDialog()

    trackSurveyEvent({
      actor: 'kepala_skb',
      eventAction: 'view',
      eventCategory: 'survey_kemudahan_perencanaan',
    })
  }

  const handleOpenKemudahanSurveyForm = () => {
    trackSurveyEvent({
      actor: 'kepala_skb',
      eventAction: 'view_gform',
      eventCategory: 'survey_kemudahan_perencanaan',
    })

    markAsSubmittedSurvey()
    onClickBackToHome()
  }

  const handleClickBackToHome = () => {
    if (tipe === 'SPNF' && isSurveyEnabled && !isSurveySubmitted) {
      handleOpenSurveyDialog()
    } else {
      onClickBackToHome()
    }
  }

  const steps = useMemo(() => {
    return [
      {
        title: 'Perencanaan akan otomatis disetujui',
        description: isPltKSRole
          ? 'Perencanaan Kinerja Anda akan otomatis disetujui dan disampaikan ke Pejabat Penilai Kinerja.'
          : 'Perencanaan Kinerja Anda akan otomatis disetujui dan disampaikan ke Pejabat Penilai Kinerja. Perencanaan juga akan otomatis dikirim ke E-Kinerja dalam 1x24 jam.',
      },
      {
        title: 'Lakukan tahap berikutnya: Pelaksanaan Kinerja',
      },
    ]
  }, [isPltKSRole])

  return (
    <>
      <div className="p-9 flex justify-center">
        <div className="w-[500px] p-9 flex flex-col">
          <ImgTermConditionTNCList width={84} height={84} />

          <Text variant="heading-lg" className="mt-4">
            Perencanaan Kinerja selesai!
          </Text>

          <div className="flex flex-col gap-4 mt-4">
            {steps.map(({ title, description }, i) => (
              <div key={title} className="flex gap-2">
                <div className="flex justify-center items-center bg-gray-80 rounded-full p-3 self-start">
                  <Text variant="body-sm" color="inverse" className="absolute">
                    {i + 1}
                  </Text>
                </div>
                <div className="flex flex-col gap-1">
                  <Text variant="action">{title}</Text>
                  {description && (
                    <Text variant="body-sm" color="subdued">
                      {description}
                    </Text>
                  )}
                </div>
              </div>
            ))}
          </div>

          <Button
            size="lg"
            color="black"
            className="mt-4"
            fullWidth
            onClick={handleClickBackToHome}
          >
            Ke Pelaksanaan Kinerja
          </Button>
        </div>
      </div>

      <SurveyDialog
        autoRedirectSeconds={5}
        formContent={{
          question:
            'Dalam skala 1-5 seberapa mudah Anda melakukan tahap Perencanaan?',
          ctaLabel: 'Kirim',
        }}
        formUrl="https://forms.gle/zmpP9W6Xe3YBRqN58"
        isOpen={isSurveyDialogOpen}
        successContent={{
          title:
            'Ceritakan ke kami lebih lengkap saran dan masukan Anda untuk tahap Perencanaan',
          ctaLabel: 'Isi Survei Perencanaan',
          ctaCloseLabel: 'x',
          getCountdownText: (secondsRemaining) =>
            `Dalam ${secondsRemaining} detik Anda akan diarahkan ke Survei Perencanaan`,
        }}
        onSubmit={handleSubmitSurvey}
        onClose={handleCloseSurveyDialog}
        onOpenFormUrl={handleOpenKemudahanSurveyForm}
      />
    </>
  )
}

export default TabRangkumanReady
